import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Printer, Download, X } from "lucide-react";
import { Button } from "../ui/button";
import { docViewer } from "../../lib/docViewer";
import { PdfCanvasViewer } from "./PdfCanvasViewer";

export const DocumentViewerHost = () => {
  const [doc, setDoc] = useState(null);
  const [url, setUrl] = useState("");
  const frameRef = useRef(null);
  const printFrameRef = useRef(null);

  useEffect(() => docViewer.subscribe(setDoc), []);

  useEffect(() => {
    if (doc?.kind !== "pdf") { setUrl(""); return undefined; }
    const u = URL.createObjectURL(doc.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [doc]);

  useEffect(() => {
    if (!doc) return undefined;
    // capture na window: fecha só o visualizador e impede que o Escape chegue ao Dialog por baixo
    const onKey = (e) => { if (e.key === "Escape") { e.stopImmediatePropagation(); e.preventDefault(); docViewer.close(); } };
    window.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey, true); document.body.style.overflow = prev; };
  }, [doc]);

  if (!doc) return null;

  const print = () => {
    const w = (doc.kind === "pdf" ? printFrameRef : frameRef).current?.contentWindow;
    try { w?.focus(); w?.print(); } catch { /* bloqueado pelo browser */ }
  };

  const download = () => {
    const blob = doc.kind === "pdf" ? doc.blob : new Blob([doc.html], { type: "text/html;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = doc.filename || (doc.kind === "pdf" ? "documento.pdf" : "documento.html");
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 10000);
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-zinc-900 pointer-events-auto" onPointerDown={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()} data-testid="document-viewer">
      <header className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-zinc-950 text-white border-b border-white/10 shrink-0">
        <Button variant="ghost" size="sm" onClick={docViewer.close} className="text-white hover:bg-white/10 hover:text-white" data-testid="document-viewer-back-btn">
          <ArrowLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Voltar</span>
        </Button>
        <h2 className="flex-1 truncate text-sm font-semibold" data-testid="document-viewer-title">{doc.title}</h2>
        <Button variant="ghost" size="sm" onClick={print} className="text-white hover:bg-white/10 hover:text-white" data-testid="document-viewer-print-btn"><Printer className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Imprimir</span></Button>
        <Button variant="ghost" size="sm" onClick={download} className="text-white hover:bg-white/10 hover:text-white" data-testid="document-viewer-download-btn"><Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Descarregar</span></Button>
        <Button variant="ghost" size="icon" onClick={docViewer.close} className="text-white hover:bg-white/10 hover:text-white" aria-label="Fechar" data-testid="document-viewer-close-btn"><X className="w-4 h-4" /></Button>
      </header>
      <div className="flex-1 min-h-0 relative">
        {doc.kind === "html" ? (
          <iframe ref={frameRef} title={doc.title} srcDoc={doc.html} className="w-full h-full bg-white" data-testid="document-viewer-frame" />
        ) : (
          <>
            <PdfCanvasViewer blob={doc.blob} />
            {url && <iframe ref={printFrameRef} title="print" src={url} className="absolute w-0 h-0 opacity-0 pointer-events-none" aria-hidden onLoad={() => doc.autoPrint && setTimeout(print, 400)} data-testid="document-viewer-print-frame" />}
          </>
        )}
      </div>
    </div>
  );
};
